import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import { convertToUTC } from '../../util/convert_timezone';
import dateService from '../../services/dateService';
import TasksAPI from '../../services/tasksService';

const SNOOZE_OPTIONS = [
  { label: 'In 1 hour', hours: 1 },
  { label: 'In 4 hours', hours: 4 },
  { label: 'Tomorrow morning', tomorrow: true },
  { label: 'Next week', days: 7 }
];

const PRIORITY_COLORS = {
  urgent: '#DC2626',
  high: '#EA580C',
  normal: '#2563EB',
  low: '#6B7280'
};

const TARGET_ROLE_LABELS = {
  owner: 'Owner',
  manager: 'Manager',
  technician: 'Technician',
  front_desk: 'Front Desk'
};

// Linkify phone numbers in text (e.g. "555-867-5309" → clickable tel: link)
const PHONE_REGEX = /(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g;

function renderWithPhoneLinks(text) {
  if (!text) return null;
  const parts = text.split(PHONE_REGEX);
  return parts.map((part, i) => {
    if (PHONE_REGEX.test(part)) {
      PHONE_REGEX.lastIndex = 0; // reset regex state
      const digits = part.replace(/[^\d+]/g, '');
      return (
        <a key={i} href={`tel:${digits}`} style={{ color: '#3B82F6', fontWeight: 600 }}>
          {part}
        </a>
      );
    }
    return part;
  });
}

class TaskActionModal extends Component {
  state = {
    showSnoozeMenu: false,
    acting: false
  };

  componentDidUpdate(prevProps) {
    if (prevProps.task?.task_id !== this.props.task?.task_id) {
      this.setState({ showSnoozeMenu: false, acting: false });
    }
  }

  handleComplete = async () => {
    const { task, user, onActionComplete } = this.props;
    this.setState({ acting: true });
    try {
      await TasksAPI.complete(task.task_id, user.User_id);
      window.dispatchEvent(new Event('refreshNavbarCounts'));
      onActionComplete();
    } catch (err) {
      console.error('TaskActionModal: Error completing task', err);
      this.setState({ acting: false });
    }
  };

  handleSnooze = async (option) => {
    const { task, onActionComplete } = this.props;
    let localTime;
    if (option.tomorrow) {
      localTime = moment().add(1, 'day').startOf('day').hour(8);
    } else if (option.days) {
      localTime = moment().add(option.days, 'days').startOf('day').hour(8);
    } else {
      localTime = moment().add(option.hours, 'hours');
    }
    const snoozeUntil = convertToUTC(localTime).format('YYYY-MM-DD HH:mm:ss');
    this.setState({ acting: true });
    try {
      await TasksAPI.snooze(task.task_id, snoozeUntil);
      window.dispatchEvent(new Event('refreshNavbarCounts'));
      onActionComplete();
    } catch (err) {
      console.error('TaskActionModal: Error snoozing task', err);
      this.setState({ acting: false });
    }
  };

  handleViewDetails = () => {
    const { task, onHide } = this.props;
    onHide();
    if (task.entity_type === 'request' && task.entity_id) {
      this.props.history.push('/requestimates', { selectRequestId: task.entity_id, fromToday: true });
    } else {
      this.props.history.push('/tasks', { selectTaskId: task.task_id, fromToday: true });
    }
  };

  render() {
    const { show, onHide, task } = this.props;
    const { showSnoozeMenu, acting } = this.state;

    if (!task) return null;

    const localDueDate = task.due_date ? dateService.fromAPIDate(task.due_date) : null;
    const isOverdue = localDueDate && localDueDate.isBefore(moment(), 'day');
    const priColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.normal;
    const hasEntity = task.entity_type && task.entity_id;

    return (
      <Modal show={show} onHide={onHide} centered>
        {/* Header: title + priority badge, red if overdue */}
        <Modal.Header closeButton>
          <Modal.Title style={{
            fontSize: 16, fontWeight: 600,
            color: isOverdue ? '#DC2626' : undefined
          }}>
            {task.title}
            {' '}
            <span className="badge" style={{ backgroundColor: priColor, color: '#fff', fontSize: '0.65rem', verticalAlign: 'middle' }}>
              {(task.priority || 'normal').toUpperCase()}
            </span>
            {task.target_role && (
              <>
                {' '}
                <span className="badge" style={{ backgroundColor: '#E5E7EB', color: '#374151', fontSize: '0.65rem', verticalAlign: 'middle' }}>
                  <i className="fa-sharp fa-regular fa-user mr-1" style={{ fontSize: '0.55rem' }} />
                  {TARGET_ROLE_LABELS[task.target_role] || task.target_role}
                </span>
              </>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Entity contact info — name, phone, email */}
          {(task.entity_name || task.entity_phone || task.entity_email) && (
            <div className="mb-3">
              {task.entity_name && (
                <div><strong>{task.entity_name}</strong></div>
              )}
              {task.entity_phone && (
                <div>
                  <a href={`tel:${task.entity_phone.replace(/[^\d+]/g, '')}`} style={{ color: '#3B82F6' }}>
                    <i className="fa-sharp fa-regular fa-phone mr-1" />{task.entity_phone}
                  </a>
                </div>
              )}
              {task.entity_email && (
                <div>
                  <a href={`mailto:${task.entity_email}`} style={{ color: '#3B82F6' }}>
                    <i className="fa-sharp fa-regular fa-envelope mr-1" />{task.entity_email}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Next Action — primary content */}
          {task.next_action && (
            <div className="mb-3" style={{
              backgroundColor: '#f8f9fa',
              borderRadius: 8,
              padding: '12px 16px',
              fontSize: '0.95rem',
              lineHeight: 1.5
            }}>
              {renderWithPhoneLinks(task.next_action)}
            </div>
          )}

          {/* Due Date */}
          {localDueDate && (
            <div className="mb-3">
              <span style={{
                color: isOverdue ? '#DC2626' : '#374151',
                fontWeight: isOverdue ? 600 : 400,
                fontSize: '0.9rem'
              }}>
                <i className="fa-sharp fa-regular fa-calendar mr-1" />
                {isOverdue ? 'Overdue: ' : 'Due: '}
                {localDueDate.format('MMM D, YYYY')}
              </span>
            </div>
          )}

          {/* Action buttons */}
          <div className="d-flex justify-content-center flex-nowrap mb-3">
            <button
              type="button"
              className="btn btn-success m-1 buttons"
              onClick={this.handleComplete}
              disabled={acting}
            >
              <i className="fa-sharp fa-solid fa-check" /> COMPLETE
            </button>
            <div className="task-snooze-dropdown">
              <button
                type="button"
                className="btn btn-warning m-1 buttons"
                style={{ color: '#fff' }}
                onClick={() => this.setState({ showSnoozeMenu: !showSnoozeMenu })}
                disabled={acting}
              >
                <i className="fa-sharp fa-regular fa-clock" /> SNOOZE
              </button>
              {showSnoozeMenu && (
                <div className="task-snooze-menu">
                  {SNOOZE_OPTIONS.map((opt, i) => (
                    <button
                      key={i}
                      className="task-snooze-option"
                      onClick={() => this.handleSnooze(opt)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {hasEntity && (
              <button
                type="button"
                className="btn btn-primary m-1 buttons"
                onClick={this.handleViewDetails}
              >
                <i className="fa-sharp fa-regular fa-arrow-up-right-from-square" /> VIEW
              </button>
            )}
          </div>

          {/* Context / Description — muted, below buttons */}
          {task.description && (
            <div style={{ borderTop: '1px solid #dee2e6', paddingTop: 12 }}>
              <small className="text-muted" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Context
              </small>
              <p className="text-muted mt-1 mb-0" style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                {task.description}
              </p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    );
  }
}

export default withRouter(TaskActionModal);
